<script setup lang="ts">
import { resolveComponent } from 'vue';
import { THEME } from '@pkg/config/misc/theme';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselStepCounter,
} from '@pkg/ui/components/ui/carousel';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import type { CarouselProps } from '@pkg/ui/components/ui/carousel/interface';
import ModularBlocksRenderer from '../../../frontend/ModularBlocksRenderer.vue';

const props = defineProps<{
  theme: THEME,
  slides?: Array<{ _key: string, content: Array<ModularBlock> }>,
  preCarouselContent?: Array<ModularBlock>,
  postCarouselContent?: Array<ModularBlock>,
  variant?: CarouselProps['variant'];
}>();

const showStepCounter = props.slides?.length && props.slides?.length > 1;

/** Host app registers PartialSection. */
const PartialSection = resolveComponent('PartialSection');
</script>

<template>
  <component :is="PartialSection" :theme="props.theme || THEME.DARK">
    <div class="c-carousel-section wrapper">
      <slot name="pre-carousel">
        <ModularBlocksRenderer :blocks="props.preCarouselContent" />
      </slot>
      <slot>
        <Carousel :variant="variant">
          <div class="c-carousel-section__wrapper">
            <CarouselContent>
              <CarouselItem v-for="slide in slides" :key="slide?._key" :variant="variant">
                <div class="c-carousel-section__content">
                  <ModularBlocksRenderer :blocks="slide?.content" />
                </div>
              </CarouselItem>
            </CarouselContent>
            <CarouselStepCounter v-if="showStepCounter" :initial-count="slides?.length" />
          </div>
        </Carousel>
      </slot>
      <slot name="post-carousel">
        <ModularBlocksRenderer :blocks="props.postCarouselContent" />
      </slot>
    </div>
  </component>
</template>

<style scoped>
.c-carousel-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;

  @screen tablet-wide {
    gap: 2rem;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    @screen tablet-wide {
      gap: 1.5rem;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 1rem;

    @screen tablet-wide {
      gap: 1.5rem;
    }
  }
}
</style>
